import { Link, useNavigate } from 'react-router-dom'
import {
  LogOut,
  User,
  Plus,
  Users,
  LayoutDashboard,
  Target,
  Cat,
  MessageSquare,
  Briefcase,
  Pickaxe,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Badge,
} from '@/components/ui'
import { useToast } from '@/hooks/ui/use-toast'
import { useUnreadCount } from '@/hooks'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

export function Navbar() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()
  const unreadCount = useUnreadCount()
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
  const displayName = fullName || user?.username
  const avatarUrl = user?.profile?.avatar_url ?? null
  const role = user?.profile?.role
  const isDeveloper = role === 'developer'
  const isCustomer = role === 'customer'

  const handleLogout = async () => {
    await logout()
    toast({
      title: 'Success!',
      description: 'Logged out successfully',
    })
    navigate('/login')
  }

  const PreviewBanner = () => (
    <div className="flex flex-row justify-center gap-2 bg-yellow-400 p-2 text-center text-sm text-black">
      <Pickaxe className="h-5 w-5" />
      This is an early preview of Masonyt and will be fully available shortly.
    </div>
  )

  return (
    <nav className="sticky top-0 z-50 flex flex-col border-b bg-background/95 shadow-sm backdrop-blur-md">
      <PreviewBanner />
      <div>
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link
              to="/dashboard"
              className="group text-xl font-bold text-primary transition-colors hover:text-primary/80"
            >
              <img src="/logo.png" alt="Logo" className="h-10 group-hover:animate-pulse" />
            </Link>
            <div className="hidden items-center space-x-2 md:flex">
              <Button variant="ghost" asChild className="group">
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 hover:bg-primary hover:text-white"
                >
                  <LayoutDashboard className="h-4 w-4 text-primary transition-transform group-hover:scale-110 group-hover:text-white" />
                  Dashboard
                </Link>
              </Button>
              <Button variant="ghost" asChild className="group">
                <Link
                  to="/projects"
                  className="flex items-center gap-2 hover:bg-primary hover:text-white"
                >
                  <Briefcase className="h-4 w-4 text-primary transition-transform group-hover:scale-110 group-hover:text-white" />
                  Projects
                </Link>
              </Button>
              <Button variant="ghost" asChild className="group">
                <Link
                  to="/developers"
                  className="flex items-center gap-2 hover:bg-primary hover:text-white"
                >
                  <Users className="h-4 w-4 text-primary transition-transform group-hover:scale-110 group-hover:text-white" />
                  Developers
                </Link>
              </Button>
              <Button variant="ghost" asChild className="group relative">
                <Link
                  to="/messages"
                  className="flex items-center gap-2 hover:bg-primary hover:text-white"
                >
                  <MessageSquare className="h-4 w-4 text-primary transition-transform group-hover:scale-110 group-hover:text-white" />
                  Messages
                  {unreadCount > 0 && (
                    <Badge className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive p-0 text-xs text-destructive-foreground">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  )}
                </Link>
              </Button>
              {isDeveloper && (
                <Button variant="ghost" asChild className="group">
                  <Link
                    to="/bounty-board"
                    className="flex items-center gap-2 hover:bg-primary hover:text-white"
                  >
                    <Target className="h-4 w-4 text-primary transition-transform group-hover:scale-110 group-hover:text-white" />
                    Bounty board
                  </Link>
                </Button>
              )}
              {isCustomer && (
                <Button variant="ghost" asChild className="group relative overflow-hidden">
                  <Link
                    to="/post"
                    className="flex items-center gap-2 hover:bg-primary hover:text-white"
                  >
                    <Plus className="h-4 w-4 text-primary transition-transform duration-300 group-hover:rotate-90 group-hover:text-white" />
                    Post Project
                  </Link>
                </Button>
              )}
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="group flex items-center gap-2">
                      <Avatar className="h-8 w-8 border border-border/70">
                        {avatarUrl ? (
                          <AvatarImage src={avatarUrl} alt={`${displayName || 'User'} avatar`} />
                        ) : (
                          <AvatarFallback>
                            <span className="text-xs font-medium uppercase">
                              {displayName?.[0] ?? <User className="h-3 w-3" />}
                            </span>
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <span className="hidden font-medium sm:inline">{displayName}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5 text-sm">
                      <div className="font-medium">{fullName || user?.username}</div>
                      {user?.email && <div className="text-muted-foreground">{user.email}</div>}
                    </div>
                    <DropdownMenuSeparator />
                    {user && (
                      <DropdownMenuItem asChild>
                        <Link to={`/profiles/${user.username}`} className="flex items-center gap-2">
                          <Cat className="h-4 w-4" />
                          Public profile
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {user && <DropdownMenuSeparator />}
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => {
                        void handleLogout()
                      }}
                      className="flex items-center gap-2 text-destructive focus:text-destructive"
                    >
                      <LogOut className="h-4 w-4" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
          <div className="pb-4 md:hidden">
            <div className="flex space-x-2 overflow-x-auto">
              <Button variant="ghost" size="sm" asChild className="flex-1">
                <Link to="/dashboard" className="flex items-center gap-2">
                  <LayoutDashboard className="h-4 w-4" />
                  Dashboard
                </Link>
              </Button>
              <Button variant="ghost" size="sm" asChild className="flex-1">
                <Link to="/projects" className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Projects
                </Link>
              </Button>
              <Button variant="ghost" size="sm" asChild className="relative flex-1">
                <Link to="/messages" className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Messages
                  {unreadCount > 0 && (
                    <Badge className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive p-0 text-[10px] text-destructive-foreground">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                  )}
                </Link>
              </Button>
              {isDeveloper && (
                <Button variant="ghost" size="sm" asChild className="flex-1">
                  <Link to="/bounty-board" className="flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Bounty
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}
